import React, { useState, useMemo } from 'react';
import './EnhancedSuggestions.css';

const EnhancedSuggestions = ({ evaluation, loading = false, error = null }) => {
    const [activeTab, setActiveTab] = useState('quick-wins');
    const [expandedCards, setExpandedCards] = useState(new Set());
    const [completedItems, setCompletedItems] = useState(new Set());

    const toggleCard = (index) => {
        const newExpanded = new Set(expandedCards);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedCards(newExpanded);
    };

    const toggleCompleted = (index) => {
        const newCompleted = new Set(completedItems);
        if (newCompleted.has(index)) {
            newCompleted.delete(index);
        } else {
            newCompleted.add(index);
        }
        setCompletedItems(newCompleted);
    };

    // Calculate progress metrics
    const progressMetrics = useMemo(() => {
        if (!evaluation) return { categories: [], totalScore: 0, totalMax: 100 };

        const breakdown = evaluation.score?.breakdown || {};
        const categories = [
            { name: 'Components', score: breakdown.components || 0, max: 25 },
            { name: 'APIs', score: breakdown.apis || 0, max: 25 },
            { name: 'Algorithms', score: breakdown.algorithms || 0, max: 25 },
            { name: 'Tradeoffs', score: breakdown.tradeoffs || 0, max: 25 },
        ];

        const totalScore = evaluation.score?.total || 0;
        const totalMax = 100;

        return { categories, totalScore, totalMax };
    }, [evaluation]);

    // Organize gaps by priority
    const organizedGaps = useMemo(() => {
        if (!evaluation) return { high: [], medium: [], low: [] };

        const gaps = evaluation.gaps || [];
        return {
            high: gaps.filter(g => g.priority === 'high'),
            medium: gaps.filter(g => g.priority === 'medium'),
            low: gaps.filter(g => g.priority === 'low'),
        };
    }, [evaluation]);

    if (loading) {
        return (
            <div className="evaluation-loading">
                <div className="loading-spinner" />
                <h3>Evaluating your design...</h3>
                <p>Analyzing components, connections, and architecture</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="evaluation-error">
                <h3>Evaluation Failed</h3>
                <p>{error}</p>
            </div>
        );
    }

    if (!evaluation) {
        return (
            <div className="no-evaluation">
                <h3>No Evaluation Yet</h3>
                <p>Submit your design to get AI-powered feedback and actionable suggestions.</p>
            </div>
        );
    }

    const renderProgressSection = () => {
        // Determine current badge based on score
        let badge = null;
        if (progressMetrics.totalScore >= 90) {
            badge = { name: 'Master', color: '#fbbf24' };
        } else if (progressMetrics.totalScore >= 75) {
            badge = { name: 'Almost Pro', color: '#60a5fa' };
        } else if (progressMetrics.totalScore >= 50) {
            badge = { name: 'Getting There', color: '#a78bfa' };
        } else if (progressMetrics.totalScore >= 25) {
            badge = { name: 'First Steps', color: '#4ade80' };
        }

        return (
            <div className="progress-journey compact">
                <div className="journey-header">
                    <div className="score-display">
                        <div className="score-circle">
                            <svg viewBox="0 0 120 120">
                                <circle cx="60" cy="60" r="54" className="score-bg" />
                                <circle
                                    cx="60"
                                    cy="60"
                                    r="54"
                                    className="score-fill"
                                    style={{
                                        strokeDasharray: `${progressMetrics.totalScore * 3.39}, 339`
                                    }}
                                />
                            </svg>
                            <div className="score-text">
                                <span className="score-value">{progressMetrics.totalScore}</span>
                                <span className="score-max">/100</span>
                            </div>
                        </div>
                        {badge && (
                            <div className="badge-label" style={{ color: badge.color }}>
                                {badge.name.toUpperCase()}
                            </div>
                        )}
                    </div>
                    <div className="score-breakdown">
                        {progressMetrics.categories.map((cat, index) => (
                            <div key={index} className="breakdown-item">
                                <div className="breakdown-header">
                                    <span className="breakdown-name">{cat.name}</span>
                                    <span className="breakdown-score">{cat.score}/{cat.max}</span>
                                </div>
                                <div className="breakdown-bar">
                                    <div
                                        className="breakdown-fill"
                                        style={{ width: `${(cat.score / cat.max) * 100}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderGapCard = (gap, index, globalIndex) => {
        const isExpanded = expandedCards.has(globalIndex);
        const isCompleted = completedItems.has(globalIndex);

        return (
            <div
                key={globalIndex}
                className={`gap-card priority-${gap.priority} ${isCompleted ? 'completed' : ''}`}
            >
                <button
                    className={`card-complete-btn ${isCompleted ? 'checked' : ''}`}
                    onClick={() => toggleCompleted(globalIndex)}
                    title={isCompleted ? 'Mark as not understood' : 'Mark as understood'}
                >
                    {isCompleted ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    ) : null}
                </button>

                <div className="card-question">
                    <span className={`priority-badge-inline ${gap.priority}`}>
                        {gap.priority}
                    </span>
                    {gap.question}
                </div>

                {gap.hint && (
                    <div className={`card-hint ${isExpanded ? 'expanded' : ''}`}>
                        <button
                            className="hint-toggle"
                            onClick={() => toggleCard(globalIndex)}
                        >
                            Hint {isExpanded ? '▼' : '▶'}
                        </button>
                        {isExpanded && (
                            <div className="hint-content">
                                {gap.hint}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderQuickWins = () => {
        const highPriority = organizedGaps.high.slice(0, 3);
        if (highPriority.length === 0) {
            return (
                <div className="empty-state">
                    <h3>Great Work!</h3>
                    <p>You've addressed all high-priority items. Check other tabs for more improvements.</p>
                </div>
            );
        }

        return (
            <div className="tab-content">
                <div className="tab-intro">
                    <h3>Focus on These First</h3>
                    <p>Address these high-impact areas to significantly improve your design.</p>
                    <div className="progress-indicator">
                        {completedItems.size > 0 && (
                            <span>✓ {completedItems.size} items understood</span>
                        )}
                    </div>
                </div>
                <div className="cards-container">
                    {highPriority.map((gap, index) => renderGapCard(gap, index, index))}
                </div>
            </div>
        );
    };

    const renderStrengths = () => {
        const strengths = evaluation.strengths || [];
        if (strengths.length === 0) return null;

        return (
            <div className="tab-content">
                <div className="tab-intro">
                    <h3>What You Nailed</h3>
                    <p>These are your strengths. Keep building on them!</p>
                </div>
                <div className="strengths-grid">
                    {strengths.map((strength, index) => (
                        <div key={index} className="strength-card">
                            <p>{strength}</p>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderAllSuggestions = () => {
        const allGaps = [
            ...organizedGaps.high.map((g, i) => ({ ...g, globalIndex: i })),
            ...organizedGaps.medium.map((g, i) => ({ ...g, globalIndex: organizedGaps.high.length + i })),
            ...organizedGaps.low.map((g, i) => ({ ...g, globalIndex: organizedGaps.high.length + organizedGaps.medium.length + i })),
        ];

        return (
            <div className="tab-content">
                <div className="tab-intro">
                    <h3>Complete Improvement Guide</h3>
                    <p>All suggestions organized by priority. Take your time and work through them systematically.</p>
                </div>

                {organizedGaps.high.length > 0 && (
                    <div className="priority-section">
                        <h4 className="section-title high">High Priority ({organizedGaps.high.length})</h4>
                        <div className="cards-container">
                            {organizedGaps.high.map((gap, index) =>
                                renderGapCard(gap, index, index)
                            )}
                        </div>
                    </div>
                )}

                {organizedGaps.medium.length > 0 && (
                    <div className="priority-section">
                        <h4 className="section-title medium">Medium Priority ({organizedGaps.medium.length})</h4>
                        <div className="cards-container">
                            {organizedGaps.medium.map((gap, index) =>
                                renderGapCard(gap, index, organizedGaps.high.length + index)
                            )}
                        </div>
                    </div>
                )}

                {organizedGaps.low.length > 0 && (
                    <div className="priority-section">
                        <h4 className="section-title low">Low Priority ({organizedGaps.low.length})</h4>
                        <div className="cards-container">
                            {organizedGaps.low.map((gap, index) =>
                                renderGapCard(gap, index, organizedGaps.high.length + organizedGaps.medium.length + index)
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderNextSteps = () => {
        const steps = evaluation.next_steps || [];
        if (steps.length === 0) return null;

        return (
            <div className="tab-content">
                <div className="tab-intro">
                    <h3>Learning Roadmap</h3>
                    <p>Recommended topics and resources to deepen your understanding.</p>
                </div>
                <div className="next-steps-list">
                    {steps.map((step, index) => (
                        <div key={index} className="next-step-item">
                            <div className="step-number">{index + 1}</div>
                            <p>{step}</p>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="enhanced-suggestions">
            <div className="suggestions-tabs">
                <button
                    className={`tab-btn ${activeTab === 'quick-wins' ? 'active' : ''}`}
                    onClick={() => setActiveTab('quick-wins')}
                >
                    <span className="tab-label">Quick Wins</span>
                    {organizedGaps.high.length > 0 && (
                        <span className="tab-badge">{organizedGaps.high.length}</span>
                    )}
                </button>
                <button
                    className={`tab-btn ${activeTab === 'strengths' ? 'active' : ''}`}
                    onClick={() => setActiveTab('strengths')}
                >
                    <span className="tab-label">Strengths</span>
                </button>
            </div>

            <div className="tab-panels">
                {activeTab === 'quick-wins' && renderQuickWins()}
                {activeTab === 'strengths' && renderStrengths()}
            </div>

            {renderProgressSection()}
        </div>
    );
};

export default EnhancedSuggestions;
